import { useSyncExternalStore } from 'react';

/**
 * The master uploads whose parts are all in S3 and whose complete call runs, by catalog row uuid.
 * The value is the file name.
 *
 * A screen that closes during the complete does not stop it (the master would be lost), so the
 * call outlives the hook that started it. A screen that opens the same row meanwhile reads this
 * store and shows the upload as still finishing, until the complete answers and the catalog
 * reloads.
 */
const pending = new Map<string, string>();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function startPendingMasterComplete(itemUuid: string, fileName: string): void {
  pending.set(itemUuid, fileName);
  notify();
}

export function endPendingMasterComplete(itemUuid: string): void {
  if (pending.delete(itemUuid)) {
    notify();
  }
}

/** The file name of the master that is still finishing for this row, or undefined. */
export function pendingMasterComplete(itemUuid: string | undefined): string | undefined {
  return itemUuid === undefined ? undefined : pending.get(itemUuid);
}

/** `pendingMasterComplete` as React state. */
export function usePendingMasterComplete(itemUuid: string | undefined): string | undefined {
  return useSyncExternalStore(subscribe, () => pendingMasterComplete(itemUuid));
}
